import React, { useState, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { Card, CardBody } from "../../../styles/components/ModernCard";
import { jsPDF } from "jspdf";
import ReportPreview from "./ReportPreview";

// Styled components
const ReportCard = styled(Card)`
  position: relative;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(148, 163, 184, 0.2);
  transition: all 0.3s ease;
  min-height: 350px;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2), 0 0 15px rgba(99, 102, 241, 0.3);
  }

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 20% 80%,
        rgba(34, 211, 238, 0.12) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 20%,
        rgba(244, 114, 182, 0.08) 0%,
        transparent 50%
      );
    border-radius: inherit;
    z-index: -1;
    opacity: 0.8;
  }
`;

const ChartTitle = styled.h3`
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.25rem;
  margin-bottom: 1rem;
  font-weight: 500;
  text-align: center;
`;

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly; /* Changed to space-evenly for better distribution */
  height: 100%;
  padding: 20px 0; /* Added padding for better spacing */
`;

const ReportIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 2rem; /* Increased margin */
  color: rgba(34, 211, 238, 0.9);
  margin-top: 1rem; /* Added top margin */

  svg {
    filter: drop-shadow(0 0 8px rgba(34, 211, 238, 0.6));
  }
`;

const ReportDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 2.5rem; /* Increased margin */
  font-size: 1rem;
  line-height: 1.5;
  max-width: 90%; /* Limit width for better readability */
  padding: 0 10px; /* Added horizontal padding */
`;

const GenerateButton = styled.button`
  background: linear-gradient(
    135deg,
    rgba(34, 211, 238, 0.8) 0%,
    rgba(56, 189, 248, 0.8) 100%
  );
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(34, 211, 238, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(34, 211, 238, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(34, 211, 238, 0.3);
  }

  &:disabled {
    background: rgba(148, 163, 184, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ReportPreviewContainer = styled.div`
  position: fixed;
  top: 0; /* Cover the entire screen including header */
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85); /* Slightly darker background */
  z-index: 10000; /* Ensures it's above header */
  display: ${(props) => (props.visible ? "flex" : "none")};
  justify-content: center;
  align-items: flex-start; /* Align from top instead of center */
  padding-top: 90px; /* Add padding to push content below header */
  padding-bottom: 30px;
  padding-left: 2rem;
  padding-right: 2rem;
  overflow-y: auto; /* Allow scrolling for the container */
  height: 100vh; /* Full viewport height */
`;

const ReportPreviewContent = styled.div`
  background: white;
  width: 21cm; /* A4 width */
  max-height: calc(100vh - 140px); /* Account for header and padding */
  margin: 0 auto;
  padding: 1.5cm; /* Reduced padding */
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow-y: auto; /* Enable vertical scrolling */
`;

const CloseButton = styled.button`
  position: fixed;
  top: 100px; /* Adjusted position to be visible below header */
  right: 25px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10002; /* Ensure it's above the modal */
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
  }
`;

const ReportHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    margin-bottom: 0.25rem;
  }
`;

const ChartSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #333;
  margin-bottom: 1rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const DownloadButton = styled.button`
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background: #1976d2;
  }

  &:disabled {
    background: #90caf9;
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  margin-right: 8px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: ${spinAnimation} 1s ease-in-out infinite;
`;

const ReportGenerator = ({
  reportStats,
  reviewTimeStats,
  courseStats,
  companyRatingStats,
  internshipCountStats,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);
  const chartRefs = useRef({});

  const generateReport = async () => {
    setGenerating(true);

    try {
      setShowPreview(true);
      // The charts will be rendered in the report preview
      // We'll handle PDF generation when the download button is clicked
    } catch (error) {
      console.error("Error generating report:", error);
    } finally {
      setGenerating(false);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };
  const downloadPDF = () => {
    // Show a loading indicator
    setDownloading(true);

    // Create a simple empty PDF (1 page)
    setTimeout(() => {
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Add timestamp to filename
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .substring(0, 19);
      const filename = `SCAD_Statistics_Report_${timestamp}.pdf`;

      // Simply save the empty document
      doc.save(filename);
      setDownloading(false);

      // Show success notification
      showDownloadSuccessNotification();
    }, 500);
  };

  // Function to show download success notification
  const showDownloadSuccessNotification = () => {
    // Create notification element
    const notification = document.createElement("div");
    notification.style.position = "fixed";
    notification.style.top = "60px"; // Position below header
    notification.style.left = "50%";
    notification.style.transform = "translateX(-50%)";
    notification.style.backgroundColor = "#4caf50";
    notification.style.color = "white";
    notification.style.padding = "10px 20px";
    notification.style.borderRadius = "4px";
    notification.style.boxShadow = "0 2px 10px rgba(0,0,0,0.2)";
    notification.style.zIndex = "10000";
    notification.style.transition = "opacity 0.5s ease";
    notification.textContent = "Report downloaded";

    // Add to body
    document.body.appendChild(notification);

    // Remove after 3 seconds
    setTimeout(() => {
      notification.style.opacity = "0";
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 500);
    }, 3000);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return (
    <>
      <ReportCard>
        <CardBody>
          <ReportContainer>
            <ChartTitle>Generate Reports</ChartTitle>
            <ReportIcon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <line x1="10" y1="9" x2="8" y2="9"></line>
              </svg>
            </ReportIcon>
            <ReportDescription>
              Generate reports based on the real-time statistics
            </ReportDescription>
            <GenerateButton onClick={generateReport} disabled={generating}>
              {generating ? "Generating..." : "Generate Report"}
            </GenerateButton>
          </ReportContainer>
        </CardBody>
      </ReportCard>{" "}
      {showPreview && (
        <ReportPreviewContainer visible={true}>
          <CloseButton onClick={closePreview} title="Close Report Preview">
            <span style={{ position: "relative", top: "-1px" }}>×</span>
          </CloseButton>
          <div ref={reportRef}>
            <ReportPreview
              reportStats={reportStats}
              reviewTimeStats={reviewTimeStats}
              courseStats={courseStats}
              companyRatingStats={companyRatingStats}
              internshipCountStats={internshipCountStats}
              onDownload={downloadPDF}
              downloading={downloading}
            />
          </div>
        </ReportPreviewContainer>
      )}
    </>
  );
};

export default ReportGenerator;
